import React, { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Close, CreateNewFolder, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { IKiVorgangProposal } from '../Interfaces/IKi';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { customFetcher } from '../hooks/useFetch';
import { useGetConfig } from '../hooks/data';
import {
  useGetUserGroups,
  useGetUsers,
} from '../hooks/reactQuerys/UserQueries';
import { useGetLegalTransactionTypes } from '../hooks/reactQuerys/LegalTransactionQueries';
import { useGetArnoTopObjects } from '../hooks/reactQuerys/ARNOtopQueries';
import {
  ILegalTransactionNewVorbelegung,
  LegalTransactionNew,
} from '../LegalTransaction/LegalTransactionNew';

interface IProps {
  proposal: IKiVorgangProposal;
}

/**
 * Vorschlagskarte: einen neuen Vorgang anlegen (#1336).
 *
 * Die KI hat Art, Beteiligte und Zuständige bestimmt — angelegt wird im
 * gewohnten Dialog „Neuer Vorgang", den die Karte vorbelegt öffnet. Dort
 * stecken ArnoTop-Abgleich, Rechnungserinnerung und die Vorgaben der
 * Vorgangsart; ein zweiter Anlageweg liefe früher oder später auseinander.
 */
export const KiVorgangProposalCard: React.FC<IProps> = ({ proposal }) => {
  const navigate = useNavigate();
  const [state, setState] = useState<'open' | 'done' | 'dismissed'>('open');
  const [vorbelegung, setVorbelegung] =
    useState<ILegalTransactionNewVorbelegung | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idNeu, setIdNeu] = useState<number | null>(null);

  if (state === 'dismissed') {
    return null;
  }

  const { Payload, Display } = proposal;

  if (state === 'done') {
    return (
      <Alert
        severity='success'
        icon={<CreateNewFolder fontSize='inherit' />}
        action={
          idNeu !== null && (
            <Button
              size='small'
              color='inherit'
              startIcon={<OpenInNew />}
              onClick={() => navigate(`/legaltransaction/${idNeu}`)}
            >
              Öffnen
            </Button>
          )
        }
      >
        Der Vorgang „{Payload.Title}" ist angelegt.
      </Alert>
    );
  }

  async function handleOeffnen() {
    setError(null);
    setIsLoading(true);

    try {
      // Der Dialog führt Mandanten als ganze Datensätze; die Karte kennt nur
      // ids. Geladen wird erst hier, nicht schon beim Anzeigen der Karte.
      const mandanten = await Promise.all(
        Payload.LegalClientArray.map(async eintrag => {
          const client: ILegalClient = await customFetcher({
            endpoint: `/legalclient/${eintrag.idLegalClient}`,
          });
          return { ...client, idLegalRole: eintrag.idLegalRole };
        })
      );

      setVorbelegung({
        idLegalTransactionType: Payload.idLegalTransactionType,
        Title: Payload.Title,
        Notary_idUser: Payload.Notary_idUser,
        Employee_idUser: Payload.Employee_idUser,
        idUserGroup: Payload.idUserGroup,
        LegalClientArray: mandanten,
      });
    } catch {
      setError('Die Beteiligten konnten nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderColor: 'primary.light' }}>
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <CreateNewFolder fontSize='small' color='primary' />
          <Typography variant='subtitle2'>Neuer Vorgang</Typography>
        </Stack>

        <Typography variant='body2'>
          <b>{Payload.Title}</b>
          {Display.Vorgangsart ? ` · ${Display.Vorgangsart}` : ''}
        </Typography>

        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
          {Display.Beteiligte.map(b => (
            <Chip
              key={`ki-vorgang-${b.Name}-${b.Rolle}`}
              size='small'
              label={b.Rolle ? `${b.Name} (${b.Rolle})` : b.Name}
            />
          ))}
          {Display.Notar && (
            <Chip
              size='small'
              variant='outlined'
              label={`Notar: ${Display.Notar}`}
            />
          )}
          {(Display.Mitarbeiter || Display.Gruppe) && (
            <Chip
              size='small'
              variant='outlined'
              label={`Zuständig: ${Display.Mitarbeiter ?? Display.Gruppe}`}
            />
          )}
        </Stack>

        {proposal.Hinweise.length > 0 && (
          <Alert severity='info' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {proposal.Hinweise.map(hinweis => (
                <Typography component='li' variant='caption' key={hinweis}>
                  {hinweis}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {error !== null && <Alert severity='error'>{error}</Alert>}

        <Stack
          direction='row'
          spacing={1}
          justifyContent='flex-end'
          alignItems='center'
        >
          {isLoading && <CircularProgress size={16} />}
          <Button
            size='small'
            color='inherit'
            startIcon={<Close />}
            disabled={isLoading}
            onClick={() => setState('dismissed')}
          >
            Verwerfen
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={<OpenInNew />}
            disabled={isLoading}
            onClick={() => void handleOeffnen()}
          >
            Im Dialog anlegen
          </Button>
        </Stack>
      </Stack>

      {vorbelegung !== null && (
        <NeuerVorgangDialog
          vorbelegung={vorbelegung}
          onClose={() => setVorbelegung(null)}
          onFinished={idLegalTransaction => {
            setVorbelegung(null);
            setIdNeu(idLegalTransaction);
            setState('done');
          }}
        />
      )}
    </Paper>
  );
};

/**
 * Lädt, was „Neuer Vorgang" braucht — mit denselben Abfragen wie die
 * Vorgangsübersicht —, und hängt den Dialog erst ein, wenn alles da ist.
 */
interface INeuerVorgangDialogProps {
  vorbelegung: ILegalTransactionNewVorbelegung;
  onClose: () => void;
  onFinished: (idLegalTransaction: number) => void;
}

const NeuerVorgangDialog: React.FC<INeuerVorgangDialogProps> = ({
  vorbelegung,
  onClose,
  onFinished,
}) => {
  const { config } = useGetConfig();
  const { users } = useGetUsers();
  const { userGroups } = useGetUserGroups();
  const { legalTransactionTypes } = useGetLegalTransactionTypes();

  const hasArnoTop = (() => {
    try {
      return config ? config.App.includes('arno_top') : false;
    } catch {
      return false;
    }
  })();
  const { arnoTopObjects } = useGetArnoTopObjects(hasArnoTop);

  if (!config || !users || !userGroups || !legalTransactionTypes) {
    return (
      <Dialog open onClose={onClose}>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <LegalTransactionNew
      config={config}
      users={users}
      userGroups={userGroups}
      legalTransactionTypes={legalTransactionTypes}
      arnoTopObjects={arnoTopObjects}
      handleClose={onClose}
      handleFinished={onFinished}
      vorbelegung={vorbelegung}
    />
  );
};
